import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { PersonAvatar } from '@/shared/components/PersonAvatar';
import type { SubscriberListRef } from '../api/subscribersApi';
import { byOwner } from '../model/byOwner';
import { countSx, enterSx, panelSx } from './styles';

type Props = { lists: SubscriberListRef[]; selected: string; onSelect: (id: string) => void };

function ListButton({ l, isOn, onSelect, delay }: { l: SubscriberListRef; isOn: boolean; onSelect: () => void; delay: number }) {
  return (
    <ButtonBase aria-current={isOn ? 'true' : undefined} onClick={onSelect} sx={{ ...rowSx(isOn), ...enterSx, animationDelay: `${delay}ms` }}>
      <Box sx={{ flexGrow: 1, minWidth: 0, textAlign: 'left' }}>
        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{l.name}</Typography>
        {l.description ? <Typography variant="caption" noWrap component="p" sx={{ color: 'text.secondary' }}>{l.description}</Typography> : null}
      </Box>
      <Box component="span" sx={countSx} aria-label={`${l.count} on this list`}>{l.count}</Box>
    </ButtonBase>
  );
}

/** Lists by who owns them: the research desk's topic lists, then each RM's client books. */
export function ListsColumn({ lists, selected, onSelect }: Props) {
  const groups = byOwner(lists);
  const order = new Map(groups.flatMap((g) => g.lists).map((l, i) => [l.id, i]));
  return (
    <Stack component="nav" aria-label="Lists" sx={{ ...panelSx, p: 1, gap: 1.5 }}>
      {groups.map((g) => (
        <Box key={g.key} component="section" aria-label={g.label}>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75, px: 1, py: 0.75 }}>
            <PersonAvatar name={g.label} size={18} />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>{g.label}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>· {g.role}</Typography>
          </Stack>
          <Stack sx={{ gap: 0.25 }}>
            {g.lists.map((l) => <ListButton key={l.id} l={l} isOn={l.id === selected} onSelect={() => { onSelect(l.id); }} delay={(order.get(l.id) ?? 0) * 30} />)}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

const rowSx = (isOn: boolean) => ({
  width: '100%',
  gap: 1,
  px: 1.25,
  py: 1,
  borderRadius: 1.25,
  border: 1,
  borderColor: isOn ? 'primary.light' : 'transparent',
  bgcolor: isOn ? 'surface.accentSoft' : 'transparent',
  transition: 'background-color 150ms, border-color 150ms',
  '&:hover': { bgcolor: isOn ? 'surface.accentSoft' : 'surface.sidebar' },
});
